import React, { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { blueGrey } from "@mui/material/colors";

import ChartComponent from "../DeckRankingTab/ChartComponent";
import TableComponent from "../DeckRankingTab/TableComponent";
import { FilterConditionContext } from "../components/FilterConditionContext";
import Footer from "../components/Footer";
import { LocationFilteredDeckRankingTabContext } from "./LocationFilteredDeckRankingTabContext";

const prefectures = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
];

export default function LocationFilteredDeckRankingTab({
  handleTabChange,
  earliestDate,
  latestDate,
}) {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [organizerOptions, setOrganizerOptions] = useState([]);

  const locationFilteredDeckRankingTabValue = useContext(
    LocationFilteredDeckRankingTabContext,
  );
  const {
    error,
    setError,
    items,
    setItems,
    prefecture,
    setPrefecture,
    organizer,
    setOrganizer,
  } = locationFilteredDeckRankingTabValue;
  const filterConditionContextValue = useContext(FilterConditionContext);
  const {
    startDate,
    currentStartDate,
    setCurrentStartDate,
    endDate,
    currentEndDate,
    setCurrentEndDate,
    dateRangeError,
    selectedDivisions,
    currentSelectedDivisions,
    setCurrentSelectedDivisions,
    isBeforeSearch,
    setIsBeforeSearch,
  } = filterConditionContextValue;

  const generateShareURL = () => {
    const params = new URLSearchParams();

    if (startDate) params.append("start_date", startDate.format("YYYY-MM-DD"));
    if (endDate) params.append("end_date", endDate.format("YYYY-MM-DD"));
    if (selectedDivisions.length > 0)
      params.append("divisions", JSON.stringify(selectedDivisions));
    if (prefecture) params.append("prefecture", prefecture);
    if (organizer) params.append("organizer", organizer);

    return (
      window.location.href + (params.toString() ? `?${params.toString()}` : "")
    );
  };

  useEffect(() => {
    const fetchOrganizers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/organizer`,
        );
        const data = await response.json();
        setOrganizerOptions(data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchOrganizers();
  }, []);

  // 検索条件が変わったときにデータをリセット
  useEffect(() => {
    if (dateRangeError) return;

    if (
      // startDateが存在 -> currentStartDateと比較
      // startDateがnull -> currentStartDateもnullならtrue
      (startDate ? startDate.isSame(currentStartDate) : !currentStartDate) &&
      (endDate ? endDate.isSame(currentEndDate) : !currentEndDate) &&
      selectedDivisions === currentSelectedDivisions
    ) {
      return;
    }

    setItems([]);
    setIsBeforeSearch(true);
    setCurrentStartDate(startDate);
    setCurrentEndDate(endDate);
    setCurrentSelectedDivisions(selectedDivisions);
  });

  const fetchData = async () => {
    if (dateRangeError) return;

    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("language", i18n.language);
      if (startDate) params.append("start_date", startDate.format("YYYY-MM-DD"));
      if (endDate) params.append("end_date", endDate.format("YYYY-MM-DD"));
      for (const division of currentSelectedDivisions) {
        params.append("division", division);
      }
      if (prefecture) params.append("prefecture", prefecture);
      if (organizer) params.append("organizer", organizer);

      const url =
        `${process.env.REACT_APP_API_URL}/deck-ranking` +
        (params.toString() ? `?${params}` : "");

      const response = await fetch(url);
      if (!response.ok) {
        const errorBody = await response.json();
        setError(
          "ERROR: " + errorBody.detail ||
          `API returned status ${response.status}`,
        );
      } else {
        const data = await response.json();
        setItems(data);
        setError(false);
        setIsBeforeSearch(false);
      }
    } catch (e) {
      // ネットワークエラーなどの問題
      console.error("An unexpected error occurred:", e);
    } finally {
      setLoading(false);
    }
  };

  let content;
  if (error) {
    content = (
      <Typography variant="body1" color={"error"} marginY={2}>
        {error}
      </Typography>
    );
  } else if (loading) {
    content = (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  } else if (isBeforeSearch) {
    content = null;
  } else if (items.length === 0) {
    content = <p>No data found.</p>;
  } else {
    content = (
      <>
        <Grid container rowGap={2}>
          <Grid item xs={12} sm={6} order={{ xs: 1, sm: 0 }}>
            <TableComponent items={items} handleTabChange={handleTabChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ChartComponent
              loading={loading}
              items={items}
              earliestDate={earliestDate}
              latestDate={latestDate}
            />
          </Grid>
        </Grid>
        <Footer generateShareURL={generateShareURL} />
      </>
    );
  }

  return (
    <>
      <Box
        display="flex"
        marginBottom={2}
        gap={2}
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
      >
        <Box flexGrow={1}>
          <Stack spacing={1}>
            <FormControlLabel
              label={
                <Typography variant="caption" marginRight={1} minWidth="120px">
                  都道府県
                </Typography>
              }
              control={
                <Autocomplete
                  options={prefectures.map((prefecture, index) => prefecture)}
                  value={prefecture}
                  onInputChange={(event, value) => {
                    setPrefecture(value);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  fullWidth
                  size="small"
                />
              }
              labelPlacement="start"
              sx={{
                marginLeft: 0,
                marginRight: 0,
                width: "100%",
                maxWidth: 400,
              }}
            />
            <FormControlLabel
              label={
                <Typography variant="caption" marginRight={1} minWidth="120px">
                  主催者
                </Typography>
              }
              control={
                <Autocomplete
                  options={organizerOptions.map((organizer) => organizer.name)}
                  value={organizer}
                  onInputChange={(event, value) => {
                    setOrganizer(value);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  fullWidth
                  size="small"
                />
              }
              labelPlacement="start"
              sx={{
                marginLeft: 0,
                marginRight: 0,
                width: "100%",
                maxWidth: 400,
              }}
            />
          </Stack>
        </Box>
        <Button
          variant="contained"
          color="button"
          onClick={() => fetchData()}
          sx={{ minWidth: 0, paddingX: 1 }}
        >
          <Search />
        </Button>
      </Box>
      {!useMediaQuery("(max-width:600px)") && (
        <Divider sx={{ marginBottom: 3 }} color={blueGrey[200]} />
      )}
      {content}
      <Footer generateShareURL={generateShareURL} />
    </>
  );
}
